import { create } from 'zustand';
import type { RoomState, JoinRoomParams, MeetingRoomEvent, BackendMeeting } from '@shared/types';
import { DEFAULT_CAPTURE_SETTINGS, DEFAULT_ROOM_STATE } from '@shared/types';
import { getRoomService, getMeetingSocket } from './container';
import { getMediaCaptureService } from '@features/media-capture/container';
import { useVideoFilterStore } from '@features/video-filters';
import { getBackendApiClient } from '@features/backend-api';

interface RoomSettings {
  /** Local participant display name. */
  participantName: string;
  tokenServerUrl: string;
}

interface RoomActions {
  createAndJoin: (roomName: string) => Promise<void>;
  joinExistingRoom: (meeting: BackendMeeting) => Promise<void>;
  joinByLink: (link: string) => Promise<void>;
  leave: () => Promise<void>;
  setMicEnabled: (enabled: boolean) => Promise<void>;
  setCameraEnabled: (enabled: boolean) => Promise<void>;
  updateSettings: (patch: Partial<RoomSettings>) => void;
  refreshRoomCredentials: () => Promise<void>;
}

type PublicRoomStore = RoomState & {
  settings: RoomSettings;
  joinedForStreamingOnly: boolean;
  /** ws_ticket от POST /meetings/{id}/join. Нужен moderationStore для
   *  авторизации HostAction-эндпоинтов, если текущий пользователь — гость,
   *  промотированный до Moderator (у него нет access_token). */
  wsTicket: string | null;
} & RoomActions;

const LIVEKIT_URL = import.meta.env.VITE_LIVEKIT_URL ?? 'wss://your-project.livekit.cloud';

const DEFAULT_SETTINGS: RoomSettings = {
  participantName: `Host-${Math.floor(Math.random() * 9000) + 1000}`,
  tokenServerUrl: import.meta.env.VITE_TOKEN_SERVER_URL ?? 'http://localhost:3001',
};

let connectQueue: Promise<void> = Promise.resolve();

export const useRoomStore = create<PublicRoomStore>((set, get) => {
  getRoomService().subscribe((roomState) => {
    set((prev) => ({
      ...roomState,
      meetingId: prev?.meetingId ?? null,
      participantId: prev?.participantId ?? null,
    }));
  });

  let lastPublishedStream: MediaStream | null = null;
  getMediaCaptureService().subscribe((snapshot) => {
    if (get().connectionState !== 'connected') return;
    if (snapshot.state !== 'active') return;

    const rawStream = getMediaCaptureService().getOutputStream();
    if (!rawStream || rawStream === lastPublishedStream) return;
    lastPublishedStream = rawStream;

    void useVideoFilterStore
      .getState()
      .processStream(rawStream)
      .then((processed) => getRoomService().publishTracks(processed));
  });

  bindMeetingSocketEvents();

  return {
    ...DEFAULT_ROOM_STATE,
    settings: DEFAULT_SETTINGS,
    joinedForStreamingOnly: false,
    wsTicket: null,

    updateSettings: (patch) =>
      set((state) => ({ settings: { ...state.settings, ...patch } })),

    createAndJoin: async (roomName) => {
      const { settings } = get();
      const meeting = await getBackendApiClient().createMeeting({ title: roomName });
      await getBackendApiClient().startMeeting(meeting.id);
      const shareLink = await getBackendApiClient().createShareLink(meeting.id, { role: 'Viewer' });
      await _joinAsHost(meeting.id, roomName, settings.participantName, shareLink.token);
    },

    joinExistingRoom: async (meeting: BackendMeeting) => {
      const { settings, meetingId: currentMeetingId } = get();
      if (currentMeetingId && currentMeetingId !== meeting.id) {
        await get().leave();
      }
      set({ error: null });
      try {
        if (meeting.status === 'Scheduled') {
          await getBackendApiClient().startMeeting(meeting.id);
        }
        const shareLink = await getBackendApiClient().createShareLink(meeting.id, { role: 'Viewer' });
        await _joinAsHost(meeting.id, meeting.title, settings.participantName, shareLink.token);
      } catch (err) {
        set({ error: err instanceof Error ? err.message : 'Failed to join room' });
        throw err;
      }
    },

    joinByLink: async (link) => {
      const parsed = parseShareLink(link);
      if (!parsed) throw new Error('Invalid or unrecognized share link');
      const { settings } = get();
      await _joinAsGuest(parsed.meetingId, parsed.shareToken, settings.participantName, link);
    },

    leave: async () => {
      getMeetingSocket().disconnect();
      await getRoomService().leave();
      set({ joinedForStreamingOnly: false, wsTicket: null, meetingId: null, participantId: null });
      useVideoFilterStore.getState().stopProcessing();
    },

    setMicEnabled: async (enabled) => {
      await getRoomService().setMicEnabled(enabled);
    },

    setCameraEnabled: async (enabled) => {
      await getRoomService().setCameraEnabled(enabled);
    },

    refreshRoomCredentials: async () => {
      const { participantId, settings } = get();

      const parsed = parseShareLink(getRoomService().getState().room!.shareUrl);
      if (!parsed) throw new Error('Invalid or unrecognized share link');

      const { token, wsTicket } = await getBackendApiClient().joinMeeting(parsed.meetingId, {
        identity: participantId!,
        displayName: settings.participantName,
        shareToken: parsed.shareToken,
      });

      useRoomStore.setState({ wsTicket });

      getMeetingSocket().connect({
        meetingId: parsed.meetingId,
        participantId: participantId!,
        wsTicket,
      });

      await _connectToLiveKit(
        get().room!.name,
        settings.participantName,
        token,
        true,
      );
    }
  };
});

export function markRoomJoinedForStreamingOnly(): void {
  useRoomStore.setState({ joinedForStreamingOnly: true });
}

async function _joinAsHost(
  meetingId: string,
  roomName: string,
  participantName: string,
  shareToken: string,
): Promise<void> {
  const identity = generateIdentity();
  const { token, participantId, wsTicket } = await getBackendApiClient().joinMeeting(meetingId, {
    identity,
    displayName: participantName,
  });

  const shareUrl = buildShareUrl(meetingId, shareToken);
  getRoomService().setRoomInfo({ id: meetingId, name: roomName, shareUrl }); // ← перенесено выше

  await _connectToLiveKit(roomName, participantName, token, false, participantId);

  useRoomStore.setState({ meetingId, participantId, wsTicket });
  getMeetingSocket().connect({ meetingId, wsTicket, participantId });
}

async function _joinAsGuest(
  meetingId: string,
  shareToken: string,
  participantName: string,
  link: string,
): Promise<void> {
  const identity = generateIdentity();
  const { token, participantId, wsTicket } = await getBackendApiClient().joinMeeting(meetingId, {
    identity,
    displayName: participantName,
    shareToken,
  });

  getRoomService().setRoomInfo({ id: meetingId, name: meetingId, shareUrl: link }); // ← перенесено выше

  await _connectToLiveKit(meetingId, participantName, token, false, participantId);

  useRoomStore.setState({ meetingId, participantId, wsTicket });
  getMeetingSocket().connect({ meetingId, wsTicket, participantId });
}

async function _connectToLiveKit(
  roomName: string,
  participantName: string,
  token: string,
  isTokenRefresh = false,
  participantId?: string,
): Promise<void> {
  const task = async () => {
    const roomService = getRoomService();
    const captureService = getMediaCaptureService();

    const params: JoinRoomParams = {
      roomName,
      participantName,
      token,
      livekitUrl: LIVEKIT_URL,
      isTokenRefresh,
      participantId,
    };

    if (!captureService.getOutputStream()) {
      await captureService.start(DEFAULT_CAPTURE_SETTINGS);
    }

    const rawLocalStream = captureService.getOutputStream();
    const localStream = rawLocalStream
      ? await useVideoFilterStore.getState().processStream(rawLocalStream)
      : null;

    await roomService.join(params, localStream);
  };

  const result = connectQueue.then(task, task);
  connectQueue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

function bindMeetingSocketEvents(): void {
  getMeetingSocket().onEvent((event: MeetingRoomEvent) => {
    if (event.type === 'MediaTokenIssued') {
      const { room, settings, participantId } = useRoomStore.getState();
      if (room) {
        void _connectToLiveKit(room.name, settings.participantName, event.token, true, participantId ?? undefined);
      }
      return;
    }
    if (event.type === 'ParticipantDenied') {
      const { participantId } = useRoomStore.getState();
      if (event.participantId === participantId) {
        void _handleForcedExit('Your request to join was denied by the host.');
      }
      return;
    }

    // Встреча завершена (или отменена) — все участники должны покинуть комнату.
    if (event.type === 'MeetingStatusChanged' && (event.status === 'Ended' || event.status === 'Cancelled')) {
      const { connectionState } = useRoomStore.getState();
      if (connectionState === 'disconnected') return; // уже вышли (например, хост сам закрыл комнату)
      void _handleForcedExit(
        event.status === 'Ended' ? 'This meeting has ended.' : 'This meeting has been cancelled.',
      );
      return;
    }

    if (event.type === 'HostAction' && event.action === 'remove_participant') {
      const { participantId } = useRoomStore.getState();
      if (event.targetId === participantId) {
        void _handleForcedExit('You have been removed from the meeting by the host.');
      }
      return;
    }
    if (event.type === 'HostAction' && (event.action === 'mute' || event.action === 'unmute')) {
      const { participantId } = useRoomStore.getState();
      if (event.targetId !== participantId) return;

      void getRoomService()
        .setMicEnabled(event.action === 'unmute')
        .catch((err) => {
          console.error('setMicEnabled retry failed, falling back to full reconnect:', err);
          return useRoomStore.getState().refreshRoomCredentials();
        })
        .catch((err) => console.error('Failed to restore microphone after unmute:', err));
      return;
    }
  });

  getMeetingSocket().onReconnectExhausted(() => {
    const { meetingId, participantId, settings } = useRoomStore.getState();
    if (!meetingId || !participantId) return;
    void getBackendApiClient()
      .joinMeeting(meetingId, { identity: participantId, displayName: settings.participantName })
      .then(({ wsTicket }) => {
        useRoomStore.setState({ wsTicket });
        getMeetingSocket().connect({ meetingId, wsTicket, participantId });
      })
      .catch(() => {
        useRoomStore.setState({
          error: 'Connection lost. Please rejoin the room.',
        });
      });
  });
}

async function _handleForcedExit(message: string): Promise<void> {
  getMeetingSocket().disconnect();
  await getRoomService().leave();
  useVideoFilterStore.getState().stopProcessing();
  useRoomStore.setState({
    joinedForStreamingOnly: false,
    wsTicket: null,
    meetingId: null,
    participantId: null,
    error: message,
  });
}

function generateIdentity(): string {
  return `p-${Math.random().toString(36).slice(2, 10)}`;
}

function buildShareUrl(meetingId: string, shareToken: string): string {
  const baseUrl = new URL(window.location.href);
  return `${baseUrl.origin}/join/${encodeURIComponent(meetingId)}?token=${encodeURIComponent(shareToken)}`;
}

function parseShareLink(link: string): { meetingId: string; shareToken: string } | null {
  try {
    const url = new URL(link.trim());
    const shareToken = url.searchParams.get('token');
    const segment = url.pathname.split('/').filter(Boolean).pop();
    if (!shareToken || !segment) return null;
    return { meetingId: segment, shareToken };
  } catch {
    return null;
  }
}